<template>
  <transition name="blur">
    <div class="send-bluffs-popup" v-if="modals.sendBluffs" @click.self="close">
      <div class="popup-content">
        <h4>{{ $t("session.sendBluffsTo") }}</h4>
        
        <!-- Error Message Display -->
        <transition name="fade">
          <div v-if="errorMessage" class="error-message">
            <font-awesome-icon icon="exclamation-triangle" />
            <span>{{ errorMessage }}</span>
            <button @click="clearError" class="close-error">
              <font-awesome-icon icon="times" />
            </button>
          </div>
        </transition>
        
        <!-- Confirmation View -->
        <transition name="fade">
          <div v-if="showConfirmation" class="confirmation-container">
            <div class="confirm-message">
              <font-awesome-icon icon="question-circle" />
              <p>{{ $t("session.confirmSendBluffs") }}</p>
              <div class="target-info">
                <strong>{{ targetPlayer ? targetPlayer.name : "" }}</strong>
                <span class="seat-number">({{ $t("session.seat") }} {{ getPlayerSeatNumber(targetPlayer) }})</span>
              </div>
            </div>
            <div class="button-group">
              <button @click="executeBluffsSend" class="btn-confirm">
                <font-awesome-icon icon="check" />
                {{ $t("common.confirm") }}
              </button>
              <button @click="cancelConfirmation" class="btn-cancel">
                <font-awesome-icon icon="times" />
                {{ $t("common.cancel") }}
              </button>
            </div>
          </div>
        </transition>
        
        <!-- Seat Number Input -->
        <transition name="fade">
          <div v-if="showSeatInput" class="seat-input-container">
            <div class="input-label">
              <font-awesome-icon icon="chair" />
              <span>{{ $t("session.enterSeatNumber") }}</span>
            </div>
            <input
              type="number"
              v-model.number="seatNumber"
              :placeholder="`1-${players.length}`"
              min="1"
              :max="players.length"
              @keyup.enter="confirmSeatNumber"
              @keyup.esc="cancelSeatInput"
              ref="seatInput"
              class="seat-input"
            />
            <div class="button-group">
              <button @click="confirmSeatNumber" class="btn-confirm">
                <font-awesome-icon icon="check" />
                {{ $t("common.confirm") }}
              </button>
              <button @click="cancelSeatInput" class="btn-cancel">
                <font-awesome-icon icon="times" />
                {{ $t("common.cancel") }}
              </button>
            </div>
          </div>
        </transition>
        
        <ul class="options" v-show="!showSeatInput && !showConfirmation">
          <li @click="sendToDemon">
            <font-awesome-icon icon="dragon" />
            {{ $t("session.sendBluffsOptions.demon") }}
          </li>
          <li @click="sendToLunatic">
            <font-awesome-icon icon="user-secret" />
            {{ $t("session.sendBluffsOptions.lunatic") }}
          </li>
          <li @click="sendBySeatNumber">
            <font-awesome-icon icon="chair" />
            {{ $t("session.sendBluffsOptions.bySeatNumber") }}
          </li>
          <li class="cancel" @click="close">
            <font-awesome-icon icon="times" />
            {{ $t("common.cancel") }}
          </li>
        </ul>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapMutations, mapState } from "vuex";

export default {
  data() {
    return {
      errorMessage: "",
      errorTimeout: null,
      showSeatInput: false,
      seatNumber: "",
      showConfirmation: false,
      targetPlayer: null,
      targetType: "",
    };
  },
  computed: {
    ...mapState(["modals"]),
    ...mapState("players", ["players"]),
  },
  methods: {
    showError(message) {
      this.errorMessage = message;
      // Auto-clear error after 4 seconds
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }
      this.errorTimeout = setTimeout(() => {
        this.clearError();
      }, 4000);
    },
    clearError() {
      this.errorMessage = "";
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
        this.errorTimeout = null;
      }
    },
    sendToDemon() {
      this.clearError();
      // Find demon player, but exclude those with "是疯子" (lunatic) reminder
      const demon = this.players.find(p => {
        if (!p.role || p.role.team !== 'demon') return false;
        
        // Check if player has lunatic reminder
        const hasLunaticReminder = p.reminders && p.reminders.some(r => {
          const reminderName = typeof r === 'string' ? r : r.name;
          return reminderName === "是疯子" || 
                 reminderName === "Is the Lunatic" ||
                 reminderName === "Is Lunatic";
        });
        
        // Only return true if it's a demon WITHOUT lunatic reminder
        return !hasLunaticReminder;
      });
      
      if (demon && demon.id) {
        this.targetPlayer = demon;
        this.targetType = "demon";
        this.showConfirmation = true;
      } else {
        this.showError(this.$t("session.noDemonFound"));
      }
    },
    sendToLunatic() {
      this.clearError();
      // Find player with "是疯子" or "Is the Lunatic" reminder
      // Reminder can be either a string (legacy) or an object with a 'name' property
      const lunatic = this.players.find(p => 
        p.reminders && p.reminders.some(r => {
          const reminderName = typeof r === 'string' ? r : r.name;
          return reminderName === "是疯子" || 
                 reminderName === "Is the Lunatic" ||
                 reminderName === "Is Lunatic";
        })
      );
      if (lunatic && lunatic.id) {
        this.targetPlayer = lunatic;
        this.targetType = "lunatic";
        this.showConfirmation = true;
      } else {
        this.showError(this.$t("session.noLunaticFound"));
      }
    },
    sendBySeatNumber() {
      this.clearError();
      this.showSeatInput = true;
      this.seatNumber = "";
      // Focus input after DOM update
      this.$nextTick(() => {
        if (this.$refs.seatInput) {
          this.$refs.seatInput.focus();
        }
      });
    },
    confirmSeatNumber() {
      this.clearError();
      
      if (!this.seatNumber || this.seatNumber < 1) {
        this.showError(this.$t("session.invalidSeatNumber"));
        return;
      }
      
      const index = this.seatNumber - 1;
      if (index >= 0 && index < this.players.length) {
        const player = this.players[index];
        if (player.id) {
          this.targetPlayer = player;
          this.targetType = "seat";
          this.showSeatInput = false;
          this.showConfirmation = true;
        } else {
          this.showError(this.$t("session.seatNotOccupied"));
        }
      } else {
        this.showError(this.$t("session.invalidSeatNumber"));
      }
    },
    cancelSeatInput() {
      this.showSeatInput = false;
      this.seatNumber = "";
      this.clearError();
    },
    executeBluffsSend() {
      if (this.targetPlayer && this.targetPlayer.id) {
        this.$store.commit("session/sendBluffsToPlayer", this.targetPlayer.id);
        this.close();
      }
    },
    cancelConfirmation() {
      this.showConfirmation = false;
      this.targetPlayer = null;
      this.targetType = "";
    },
    getPlayerSeatNumber(player) {
      if (!player) return "";
      const index = this.players.indexOf(player);
      return index >= 0 ? index + 1 : "";
    },
    close() {
      this.clearError();
      this.showSeatInput = false;
      this.showConfirmation = false;
      this.seatNumber = "";
      this.targetPlayer = null;
      this.targetType = "";
      this.toggleModal("sendBluffs");
    },
    ...mapMutations(["toggleModal"]),
  },
};
</script>

<style lang="scss" scoped>
@import "../../vars.scss";

.send-bluffs-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 60px 20px 20px 20px;
  
  .popup-content {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.85) 100%);
    border-radius: 10px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(10px);
    padding: 0;
    min-width: 280px;
    max-width: 350px;
    
    h4 {
      margin: 0;
      padding: 15px 20px;
      text-align: center;
      font-size: 16px;
      font-weight: 600;
      border-bottom: 2px solid rgba(255, 255, 255, 0.2);
      color: white;
    }
    
    .error-message {
      margin: 15px;
      padding: 12px 15px;
      background: linear-gradient(135deg, rgba(231, 76, 60, 0.9) 0%, rgba(192, 57, 43, 0.9) 100%);
      border-radius: 8px;
      border: 2px solid rgba(255, 107, 107, 0.5);
      box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
      display: flex;
      align-items: center;
      gap: 10px;
      color: white;
      font-size: 14px;
      position: relative;
      animation: shake 0.5s;
      
      svg {
        width: 16px;
        flex-shrink: 0;
      }
      
      span {
        flex: 1;
        line-height: 1.4;
      }
      
      .close-error {
        background: transparent;
        border: none;
        color: white;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.7;
        transition: opacity 0.2s;
        
        svg {
          width: 14px;
        }
        
        &:hover {
          opacity: 1;
        }
      }
    }
    
    .confirmation-container {
      margin: 15px;
      padding: 20px;
      background: rgba(0, 0, 0, 0.85);
      border-radius: 8px;
      border: 2px solid rgba(255, 255, 255, 0.25);
      backdrop-filter: blur(10px);
      
      .confirm-message {
        text-align: center;
        margin-bottom: 15px;
        
        svg {
          width: 32px;
          height: 32px;
          color: #f39c12;
          margin-bottom: 10px;
        }
        
        p {
          margin: 10px 0;
          color: rgba(255, 255, 255, 0.9);
          font-size: 14px;
          line-height: 1.5;
        }
        
        .target-info {
          margin-top: 12px;
          padding: 10px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 6px;
          
          strong {
            display: block;
            color: white;
            font-size: 16px;
            margin-bottom: 4px;
          }
          
          .seat-number {
            color: rgba(255, 255, 255, 0.7);
            font-size: 13px;
          }
        }
      }
      
      .button-group {
        display: flex;
        gap: 10px;
        
        button {
          flex: 1;
          padding: 10px 16px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          white-space: nowrap;
          
          svg {
            width: 14px;
            flex-shrink: 0;
          }
          
          &.btn-confirm {
            background: linear-gradient(135deg, #00a550 0%, #008040 100%);
            color: white;
            box-shadow: 0 2px 8px rgba(0, 165, 80, 0.3);
            
            &:hover {
              background: linear-gradient(135deg, #00c060 0%, #00a550 100%);
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0, 165, 80, 0.5);
            }
            
            &:active {
              transform: translateY(0);
            }
          }
          
          &.btn-cancel {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
            color: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.2);
            
            &:hover {
              background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);
              color: white;
              border-color: rgba(255, 255, 255, 0.3);
            }
          }
        }
      }
    }
    
    .seat-input-container {
      margin: 15px;
      padding: 20px;
      background: rgba(0, 0, 0, 0.85);
      border-radius: 8px;
      border: 2px solid rgba(255, 255, 255, 0.25);
      backdrop-filter: blur(10px);
      
      .input-label {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;
        color: white;
        font-size: 14px;
        font-weight: 500;
        
        svg {
          width: 16px;
          opacity: 0.8;
        }
      }
      
      .seat-input {
        width: 100%;
        padding: 12px 16px;
        font-size: 18px;
        font-weight: bold;
        text-align: center;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-radius: 8px;
        background: rgba(0, 0, 0, 0.3);
        color: white;
        box-sizing: border-box;
        transition: all 0.3s;
        
        &:focus {
          outline: none;
          border-color: rgba(74, 144, 226, 0.8);
          background: rgba(0, 0, 0, 0.5);
          box-shadow: 0 0 15px rgba(74, 144, 226, 0.3);
        }
        
        &::placeholder {
          color: rgba(255, 255, 255, 0.4);
          font-weight: normal;
        }
        
        // Remove number input spinners
        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        -moz-appearance: textfield;
      }
      
      .button-group {
        display: flex;
        gap: 10px;
        margin-top: 15px;
        
        button {
          flex: 1;
          padding: 10px 12px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          white-space: nowrap;
          
          svg {
            width: 14px;
            flex-shrink: 0;
          }
          
          &.btn-confirm {
            background: linear-gradient(135deg, #00a550 0%, #008040 100%);
            color: white;
            box-shadow: 0 2px 8px rgba(0, 165, 80, 0.3);
            
            &:hover {
              background: linear-gradient(135deg, #00c060 0%, #00a550 100%);
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0, 165, 80, 0.5);
            }
            
            &:active {
              transform: translateY(0);
            }
          }
          
          &.btn-cancel {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
            color: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(255, 255, 255, 0.2);
            
            &:hover {
              background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);
              color: white;
              border-color: rgba(255, 255, 255, 0.3);
            }
          }
        }
      }
    }
    
    ul.options {
      list-style: none;
      margin: 0;
      padding: 0;
      
      li {
        padding: 12px 20px;
        cursor: pointer;
        transition: all 0.2s;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        gap: 12px;
        
        svg {
          width: 18px;
          opacity: 0.8;
        }
        
        &:hover {
          background: rgba(255, 255, 255, 0.15);
          padding-left: 25px;
        }
        
        &:last-child {
          border-bottom: none;
        }
        
        &.cancel {
          color: #ff6b6b;
          border-top: 2px solid rgba(255, 255, 255, 0.2);
          
          &:hover {
            background: rgba(255, 107, 107, 0.2);
          }
        }
      }
    }
  }
}

.blur-enter-active,
.blur-leave-active {
  transition: opacity 0.2s;
  .popup-content {
    transition: transform 0.2s, opacity 0.2s;
  }
}

.blur-enter,
.blur-leave-to {
  opacity: 0;
  .popup-content {
    transform: translateY(-20px);
    opacity: 0;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
}
</style>
